<template>
  <div
    class="occupancy-pricing-editor"
    :class="{ 'occupancy-pricing-editor--compact': compact }"
  >
    <v-btn-toggle
      v-if="showMode"
      :value="pricingMode"
      mandatory
      dense
      color="primary"
      class="mb-4"
      aria-label="Modo de precificação"
      @change="setPricingMode"
    >
      <v-btn value="flat">
        Valor único
      </v-btn>
      <v-btn value="occupancy">
        Por ocupação
      </v-btn>
    </v-btn-toggle>

    <template v-if="showOccupancy && (pricingMode === 'occupancy' || !showMode)">
      <div class="d-flex align-center justify-space-between mb-2">
        <div>
          <div class="subtitle-2 font-weight-medium">
            Variações de ocupação
          </div>
          <div class="caption grey--text text--darken-1">
            {{ sparse ? 'Valores em cinza são herdados da configuração da categoria. Preencha apenas o que mudar.' : 'Defina o valor de cada composição de hóspedes.' }}
          </div>
        </div>
        <v-btn
          v-if="!sparse"
          small
          text
          color="primary"
          aria-label="Adicionar variação de ocupação"
          @click="addOccupancyRate"
        >
          <v-icon
            left
            small
          >
            {{ icons.mdiPlus }}
          </v-icon>
          Adicionar
        </v-btn>
      </div>

      <div
        v-if="occupancyRates.length"
        class="pricing-rows"
      >
        <div
          v-for="(rate, index) in occupancyRates"
          :key="rate.id || `occupancy-${index}`"
          class="pricing-row"
        >
          <v-text-field
            v-model="rate.name"
            label="Nome"
            outlined
            dense
            hide-details="auto"
            :readonly="sparse"
            class="pricing-row__name"
          ></v-text-field>
          <v-text-field
            v-if="!compact"
            v-model.number="rate.occupancy"
            label="Capacidade"
            type="number"
            min="1"
            outlined
            dense
            hide-details="auto"
            :readonly="sparse"
            suffix="hóspedes"
            @input="syncAdultCapacity(rate, $event)"
          ></v-text-field>
          <v-text-field
            v-model.number="rate.value"
            label="Diária"
            :placeholder="occupancyRatePlaceholder(rate, 'value')"
            :persistent-placeholder="sparse"
            type="number"
            min="0"
            prefix="R$"
            outlined
            dense
            clearable
            hide-details="auto"
          ></v-text-field>
          <v-text-field
            v-if="!compact"
            v-model.number="rate.weekend_value"
            label="FDS / fer."
            :placeholder="occupancyRatePlaceholder(rate, 'weekend_value')"
            :persistent-placeholder="sparse"
            type="number"
            min="0"
            prefix="R$"
            outlined
            dense
            clearable
            hide-details="auto"
          ></v-text-field>
          <v-tooltip
            v-if="!sparse"
            top
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                color="error"
                aria-label="Remover variação de ocupação"
                v-bind="attrs"
                v-on="on"
                @click="removeOccupancyRate(index)"
              >
                <v-icon small>
                  {{ icons.mdiDeleteOutline }}
                </v-icon>
              </v-btn>
            </template>
            <span>Remover variação</span>
          </v-tooltip>
        </div>
      </div>
      <v-alert
        v-else
        type="info"
        text
        dense
        class="mb-4"
      >
        {{ sparse ? 'A categoria não possui variações de ocupação configuradas.' : 'Adicione ao menos uma variação de ocupação.' }}
      </v-alert>
    </template>

    <div
      v-if="partialPolicies.length"
      class="mt-4"
    >
      <div class="subtitle-2 font-weight-medium">
        Adicional infantil por noite
      </div>
      <div class="caption grey--text text--darken-1 mb-2">
        {{ sparse ? 'Deixe vazio para herdar o adicional padrão.' : 'Aplicado às faixas configuradas como tarifa parcial.' }}
      </div>
      <v-row dense>
        <v-col
          v-for="policy in partialPolicies"
          :key="policy.id"
          cols="12"
          sm="6"
          md="4"
        >
          <v-text-field
            :value="childRateValue(policy.id)"
            :label="policyLabel(policy)"
            type="number"
            min="0"
            prefix="R$"
            outlined
            dense
            clearable
            hide-details="auto"
            @input="setChildRate(policy.id, $event)"
          ></v-text-field>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import { mdiDeleteOutline, mdiPlus } from '@mdi/js'

export default {
  name: 'OccupancyPricingEditor',
  props: {
    value: { type: Object, required: true },
    childPolicies: { type: Array, default: () => [] },
    defaultOccupancyRates: { type: Array, default: () => [] },
    defaultChildRates: { type: Array, default: () => [] },
    showMode: { type: Boolean, default: true },
    showOccupancy: { type: Boolean, default: true },
    sparse: { type: Boolean, default: false },
    compact: { type: Boolean, default: false },
    modeKey: { type: String, default: 'pricing_mode' },
    occupancyRatesKey: { type: String, default: 'occupancy_rates' },
    childRatesKey: { type: String, default: 'child_rates' },
  },
  data: () => ({
    icons: { mdiDeleteOutline, mdiPlus },
  }),
  computed: {
    pricingMode() {
      return this.value[this.modeKey] || 'flat'
    },
    occupancyRates() {
      return this.value[this.occupancyRatesKey] || []
    },
    childRates() {
      return this.value[this.childRatesKey] || []
    },
    partialPolicies() {
      return this.childPolicies.filter(policy => policy.charge_type === 'partial' && policy.id)
    },
  },
  methods: {
    setPricingMode(mode) {
      this.$set(this.value, this.modeKey, mode)
      if (mode === 'occupancy' && !this.occupancyRates.length && !this.sparse) {
        this.addOccupancyRate()
      }
    },
    addOccupancyRate() {
      const rates = [...this.occupancyRates]
      const occupancy = rates.length + 1
      rates.push({
        name: occupancy === 1 ? 'Individual' : `${occupancy} hóspedes`,
        occupancy,
        adults: occupancy,
        value: null,
        weekend_value: null,
      })
      this.$set(this.value, this.occupancyRatesKey, rates)
    },
    removeOccupancyRate(index) {
      const rates = [...this.occupancyRates]
      rates.splice(index, 1)
      this.$set(this.value, this.occupancyRatesKey, rates)
    },
    syncAdultCapacity(rate, occupancy) {
      this.$set(rate, 'adults', Number(occupancy))
    },
    occupancyRatePlaceholder(rate, field) {
      if (!this.sparse) return ''

      const defaultRate = this.defaultOccupancyRates.find(
        item => Number(item.occupancy) === Number(rate.occupancy),
      )
      const value = defaultRate && defaultRate[field]

      return value === null || value === undefined || value === '' ? '' : String(value)
    },
    childRateValue(policyId) {
      const rate = this.childRates.find(item => Number(item.policy_id) === Number(policyId))
      if (rate && rate.value !== null && rate.value !== undefined && rate.value !== '') {
        return rate.value
      }
      const defaultRate = this.defaultChildRates.find(item => Number(item.policy_id) === Number(policyId))

      return defaultRate ? defaultRate.value : null
    },
    setChildRate(policyId, value) {
      const rates = [...this.childRates]
      const index = rates.findIndex(item => Number(item.policy_id) === Number(policyId))
      const normalized = value === '' || value === null ? null : Number(value)
      if (index === -1) {
        rates.push({ policy_id: policyId, value: normalized })
      } else {
        this.$set(rates[index], 'value', normalized)
      }
      this.$set(this.value, this.childRatesKey, rates)
    },
    policyLabel(policy) {
      const end = policy.max_age === null || policy.max_age === undefined || policy.max_age === ''
        ? 'em diante'
        : `a ${policy.max_age}`

      return `${policy.min_age} ${end}`
    },
  },
}
</script>

<style scoped>
.pricing-rows {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pricing-row {
  display: grid;
  grid-template-columns: minmax(130px, 1.4fr) repeat(3, minmax(100px, 1fr)) 36px;
  gap: 8px;
  align-items: start;
}

.occupancy-pricing-editor--compact .pricing-row {
  grid-template-columns: minmax(110px, 1fr) minmax(100px, 1fr);
  padding: 12px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
}

.occupancy-pricing-editor--compact .pricing-row__name {
  grid-column: auto;
}

@media (max-width: 760px) {
  .pricing-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    padding: 12px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 8px;
  }

  .pricing-row__name {
    grid-column: 1 / -1;
  }
}
</style>
